import { initialState, transition } from "./machine.ts";
import { render } from "./render.ts";
import { difficultyFor } from "./difficulty.ts";
import { nextQuestion } from "./questions.ts";
import { loadLeaderboard, saveLeaderboard } from "./storage.ts";
import { playSound, startHum, stopHum } from "./audio.ts";
import type { GameEvent, GameState } from "./types.ts";

type TimerId = ReturnType<typeof setTimeout>;
type InputKind = "y" | "up" | "down" | "other";

const sceneEl = document.getElementById("scene");
if (!sceneEl) throw new Error("#scene element not found");

let state: GameState = { ...initialState(), leaderboard: loadLeaderboard() };
let pendingTimer: TimerId | null = null;
let birdAnimResetTimer: TimerId | null = null;

function clearAllTimers(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (birdAnimResetTimer !== null) {
    clearTimeout(birdAnimResetTimer);
    birdAnimResetTimer = null;
  }
}

function scheduleTimerFor(phase: GameState["phase"]): void {
  if (phase === "ASKING") {
    const { delayMin, delayMax } = difficultyFor(state.round);
    const delay = delayMin + Math.random() * (delayMax - delayMin);
    pendingTimer = setTimeout(() => {
      dispatch({ type: "TIMER_FIRED", now: performance.now() });
    }, delay);
  } else if (phase === "PROMPT") {
    const { windowMs } = difficultyFor(state.round);
    pendingTimer = setTimeout(() => {
      dispatch({ type: "TIMER_FIRED" });
    }, windowMs);
  } else if (phase === "FEEDBACK" || phase === "PENALTY") {
    pendingTimer = setTimeout(() => {
      dispatch({
        type: "TIMER_FIRED",
        question: nextQuestion(state.round + 1),
      });
    }, 800);
  } else if (phase === "GAME_OVER") {
    pendingTimer = setTimeout(() => {
      dispatch({
        type: "TIMER_FIRED",
        date: new Date().toISOString().slice(0, 10),
      });
    }, 1500);
  }
}

function playSoundForTransition(prev: GameState, next: GameState): void {
  if (prev.phase === "PROMPT" && next.phase === "FEEDBACK") {
    if (next.lastFeedback === "PERFECT") playSound("perfect");
    else if (next.lastFeedback === "GOOD") playSound("good");
    else playSound("ok");
  }
  if (next.phase === "PENALTY" && prev.phase !== "PENALTY") {
    playSound(next.lastFeedback === "TOO EARLY" ? "too_early" : "too_late");
  }
  if (prev.phase !== "GAME_OVER" && next.phase === "GAME_OVER") {
    playSound("game_over");
    stopHum();
  }
  if (prev.phase === "GAME_OVER" && next.phase === "IDLE") {
    startHum();
  }
  if (prev.phase === "LEADERBOARD_ENTRY" && next.phase === "IDLE") {
    const ls = (globalThis as { localStorage?: Storage }).localStorage ?? null;
    saveLeaderboard(ls, next.leaderboard);
    startHum();
  }
  if (next.birdAnim === "peck" && prev.birdAnim !== "peck") {
    playSound("peck");
  }
}

function repaintScene(): void {
  const { segments } = render(state);
  while (sceneEl!.firstChild) sceneEl!.removeChild(sceneEl!.firstChild);
  for (const seg of segments) {
    if (seg.highlight) {
      const span = document.createElement("span");
      span.className = "key-hit";
      span.textContent = seg.text;
      sceneEl!.appendChild(span);
    } else {
      sceneEl!.appendChild(document.createTextNode(seg.text));
    }
  }
}

function dispatch(event: GameEvent): void {
  const prev = state;
  const next = transition(state, event);
  if (next === prev) return;
  clearAllTimers();
  state = next;
  playSoundForTransition(prev, next);
  repaintScene();

  if (next.birdAnim === "peck") {
    birdAnimResetTimer = setTimeout(() => {
      state = { ...state, birdAnim: "idle" };
      repaintScene();
    }, 320);
  }

  scheduleTimerFor(next.phase);
}

function handleInput(kind: InputKind): void {
  if (kind === "y") {
    playSound("key_press");
    const event: GameEvent = { type: "KEY_Y", now: performance.now() };
    if (state.phase === "IDLE") event.question = nextQuestion(1);
    if (state.phase === "LEADERBOARD_ENTRY" && state.initialsCursor >= 2) {
      event.date = new Date().toISOString().slice(0, 10);
    }
    dispatch(event);
  } else if (kind === "up") {
    dispatch({ type: "KEY_ARROW_UP" });
  } else if (kind === "down") {
    dispatch({ type: "KEY_ARROW_DOWN" });
  } else {
    dispatch({ type: "KEY_OTHER" });
  }
}

document.addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (e.key === "y" || e.key === "Y") handleInput("y");
  else if (e.key === "ArrowUp") handleInput("up");
  else if (e.key === "ArrowDown") handleInput("down");
  else handleInput("other");
});

function bindButton(id: string, kind: InputKind): void {
  const el = document.getElementById(id);
  if (!el) return;
  // pointerdown beats click on touch (no 300ms delay) and works for mouse too
  el.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    handleInput(kind);
  });
}

bindButton("btn-y", "y");
bindButton("btn-up", "up");
bindButton("btn-down", "down");

repaintScene();
// Audio context needs a user gesture; start the hum on the first input of any kind.
const startHumOnce = (): void => {
  startHum();
};
document.addEventListener("keydown", startHumOnce, { once: true });
document.addEventListener("pointerdown", startHumOnce, { once: true });
